import React, { useState } from "react";
import { Box, Button, Card, Skeleton, Stack, Typography } from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis } from "recharts";

const PrioritySlaCharts = ({ ticketPriorityData, slaReportData, loading = false }) => {
  const [showPieTable, setShowPieTable] = useState(false);
  const [showBarTable, setShowBarTable] = useState(false);
  const pieData = [
    { name: "Assigned", value: ticketPriorityData?.assignedCount || 0, color: "#8979ff" },
    { name: "Reassigned", value: ticketPriorityData?.reAssignedCount || 0, color: "#ff928a" },
    { name: "Completed", value: ticketPriorityData?.completedCount || 0, color: "#3bc3de" },
    { name: "Reject", value: ticketPriorityData?.rejectCount || 0, color: "#ffae4c" },
    { name: "Unassigned", value: ticketPriorityData?.unassignedCount || 0, color: "#527ef0" },
  ];

  const barData = [
    { name: "Within SLA", open: slaReportData?.open?.within || 0, close: slaReportData?.closed?.within || 0 },
    { name: "Overdue KPI", open: slaReportData?.open?.without || 0, close: slaReportData?.closed?.without || 0 },
  ];

  const renderLegend = (items) => (
    <Stack direction="row" spacing={0} flexWrap="wrap" justifyContent="center">
      {items.map((item, index) => (
        <Stack key={index} direction="row" spacing={0.5} alignItems="center" sx={{ p: 0.5 }}>
          <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ width: 8, height: 8, bgcolor: item.color, borderRadius: item.shape === "circle" ? "50%" : 0, border: "1px solid #ffffff" }} />
          </Box>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 400, color: "rgba(0, 0, 0, 0.7)" }}>
            {item.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const pieLegendItems = [
    { color: "#8979ff", label: "Assigned", shape: "circle" },
    { color: "#ff928a", label: "Reassigned", shape: "circle" },
    { color: "#3bc3de", label: "Completed", shape: "circle" },
    { color: "#ffae4c", label: "Reject", shape: "circle" },
    { color: "#527ef0", label: "Unassigned", shape: "circle" },
  ];

  const barLegendItems = [
    { color: "#0d9488", label: "Open", shape: "square" },
    { color: "#ef4444", label: "Close", shape: "square" },
  ];

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box sx={{ bgcolor: "rgba(15, 23, 40, 0.9)", borderRadius: 1, px: 1.5, py: 1, boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 400, color: "#ffffff" }}>
            {payload[0].name} {payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const openItem = payload.find((p) => p.dataKey === "open");
      const closeItem = payload.find((p) => p.dataKey === "close");
      return (
        <Box sx={{ bgcolor: "#0f1728", borderRadius: 1, px: 1.25, py: 1, boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)" }}>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 600, color: "#ffffff", mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 500, color: "#a7f3d0" }}>
            Open: {openItem ? openItem.value : 0}
          </Typography>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 500, color: "#fecaca" }}>
            Close: {closeItem ? closeItem.value : 0}
          </Typography>
          <Typography sx={{ fontFamily: "Inter, Helvetica", fontSize: 12, fontWeight: 500, color: "#e5e7eb" }}>
            Total: {(openItem ? openItem.value : 0) + (closeItem ? closeItem.value : 0)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Stack direction="row" spacing={1.5} sx={{ width: "100%" }} className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
        <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2.75, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 336 }}>
            <Skeleton variant="circular" width={200} height={200} />
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center">
            {[...Array(5)].map((_, i) => (
              <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                <Skeleton variant="rounded" width={8} height={8} />
                <Skeleton variant="text" width={60} height={16} />
              </Stack>
            ))}
          </Stack>
        </Card>
        <Card elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pt: 4, pb: 2, px: 2, flex: 1 }}>
          <Box sx={{ width: "100%", px: 2 }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={18} sx={{ my: 2 }} />
            ))}
          </Box>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ width: "100%", mt: 2 }}>
            {[0,1].map((i) => (
              <Stack key={i} direction="row" spacing={0.5} alignItems="center">
                <Skeleton variant="rounded" width={12} height={12} />
                <Skeleton variant="text" width={50} height={16} />
              </Stack>
            ))}
          </Stack>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1.5} sx={{ width: "100%" }} className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
      <Card 
        elevation={0} 
        sx={{ 
          position: "relative", 
          border: 1, 
          borderColor: "divider", 
          borderRadius: 2, 
          p: 2.75, 
          display: "flex", 
          flexDirection: "column", 
          gap: 2, 
          flex: 1,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            borderColor: "#00675d",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 1 }}>
          <Typography 
            sx={{ 
              fontFamily: "Inter, Helvetica", 
              fontSize: "18px", 
              fontWeight: 600, 
              color: "#1f2937"
            }}
          >
            Ticket Priority Report
          </Typography>
          <Stack direction="row" spacing={0} sx={{ border: "1px solid #00675d", borderRadius: "6px", overflow: "hidden" }}>
            <Button
              size="small"
              className={!showPieTable ? "addButton" : undefined}
              variant={!showPieTable ? "contained" : "outlined"}
              onClick={() => setShowPieTable(false)}
              sx={{ 
                minWidth: "60px",
                borderRadius: 0,
                ...(showPieTable ? { 
                  color: '#00675d', 
                  borderColor: '#00675d', 
                  backgroundColor: "transparent",
                  '&:hover': { 
                    borderColor: '#00675d', 
                    color: '#00675d',
                    backgroundColor: "rgba(0, 103, 93, 0.08)"
                  } 
                } : {})
              }}
            >
              Chart
            </Button>
            <Button
              size="small"
              className={showPieTable ? "addButton" : undefined}
              variant={showPieTable ? "contained" : "outlined"}
              onClick={() => setShowPieTable(true)}
              sx={{ 
                minWidth: "60px",
                borderRadius: 0,
                borderLeft: showPieTable ? "none" : "1px solid #00675d",
                ...(!showPieTable ? { 
                  color: '#00675d', 
                  borderColor: '#00675d', 
                  backgroundColor: "transparent",
                  '&:hover': { 
                    borderColor: '#00675d', 
                    color: '#00675d',
                    backgroundColor: "rgba(0, 103, 93, 0.08)"
                  } 
                } : {})
              }}
            >
              Table
            </Button>
          </Stack>
        </Box>
        <Box sx={{ mt: 2 }}>
        {showPieTable ? (
          <Box sx={{ px: 1, pb: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Status</th>
                  <th style={{ textAlign: "right", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {pieData.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>{row.name}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6", textAlign: "right", color: row.color }}>{row.value}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: 8, fontWeight: 600 }}>Total</td>
                  <td style={{ padding: 8, fontWeight: 600, textAlign: "right" }}>{pieData.reduce((s, r) => s + (r.value || 0), 0)}</td>
                </tr>
              </tbody>
            </table>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={336}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={120} paddingAngle={2} dataKey="value">
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        )}
        </Box>
        {renderLegend(pieLegendItems)}
      </Card>
      <Card 
        elevation={0} 
        sx={{ 
          position: "relative", 
          border: 1, 
          borderColor: "divider", 
          borderRadius: 2, 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "stretch", 
          justifyContent: "space-between", 
          pt: 3, 
          pb: 2, 
          px: 2, 
          flex: 1,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            borderColor: "#00675d",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, pb: 1 }}>
          <Typography 
            sx={{ 
              fontFamily: "Inter, Helvetica", 
              fontSize: "18px", 
              fontWeight: 600, 
              color: "#1f2937"
            }}
          >
            SLA Report
          </Typography>
          <Stack direction="row" spacing={0} sx={{ border: "1px solid #00675d", borderRadius: "6px", overflow: "hidden" }}>
            <Button
              size="small"
              className={!showBarTable ? "addButton" : undefined}
              variant={!showBarTable ? "contained" : "outlined"}
              onClick={() => setShowBarTable(false)}
              sx={{ 
                minWidth: "60px",
                borderRadius: 0,
                ...(showBarTable ? { 
                  color: '#00675d', 
                  borderColor: '#00675d', 
                  backgroundColor: "transparent",
                  '&:hover': { 
                    borderColor: '#00675d', 
                    color: '#00675d',
                    backgroundColor: "rgba(0, 103, 93, 0.08)"
                  } 
                } : {})
              }}
            >
              Chart
            </Button>
            <Button
              size="small"
              className={showBarTable ? "addButton" : undefined}
              variant={showBarTable ? "contained" : "outlined"}
              onClick={() => setShowBarTable(true)}
              sx={{ 
                minWidth: "60px",
                borderRadius: 0,
                borderLeft: showBarTable ? "none" : "1px solid #00675d",
                ...(!showBarTable ? { 
                  color: '#00675d', 
                  borderColor: '#00675d', 
                  backgroundColor: "transparent",
                  '&:hover': { 
                    borderColor: '#00675d', 
                    color: '#00675d',
                    backgroundColor: "rgba(0, 103, 93, 0.08)"
                  } 
                } : {})
              }}
            >
              Table
            </Button>
          </Stack>
        </Box>
        <Box sx={{ mt: 2 }}>
        {showBarTable ? (
          <Box sx={{ px: 1, pb: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb" }}>
                  <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Name</th>
                  <th style={{ textAlign: "right", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Open</th>
                  <th style={{ textAlign: "right", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Close</th>
                  <th style={{ textAlign: "right", padding: 8, borderBottom: "1px solid #e5e7eb" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {barData.map((row, i) => (
                  <tr key={i}>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6" }}>{row.name}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6", textAlign: "right", color: "#0d9488" }}>{row.open}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6", textAlign: "right", color: "#ef4444" }}>{row.close}</td>
                    <td style={{ padding: 8, borderBottom: "1px solid #f3f4f6", textAlign: "right" }}>{(row.open || 0) + (row.close || 0)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ padding: 8, fontWeight: 600 }}>Total</td>
                  <td style={{ padding: 8, fontWeight: 600, textAlign: "right" }}>{barData.reduce((s, r) => s + (r.open || 0), 0)}</td>
                  <td style={{ padding: 8, fontWeight: 600, textAlign: "right" }}>{barData.reduce((s, r) => s + (r.close || 0), 0)}</td>
                  <td style={{ padding: 8, fontWeight: 600, textAlign: "right" }}>{barData.reduce((s, r) => s + ((r.open || 0) + (r.close || 0)), 0)}</td>
                </tr>
              </tbody>
            </table>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "rgba(0, 0, 0, 0.7)" }} axisLine={{ stroke: "rgba(0, 0, 25, 0.3)" }} />
              <YAxis tick={{ fontSize: 12, fill: "rgba(0, 0, 0, 0.7)" }} axisLine={false} tickLine={false} />
              <RechartsTooltip content={<CustomBarTooltip />} />
              <Bar dataKey="open" fill="#0d9488" barSize={70} radius={[6,6,0,0]} />
              <Bar dataKey="close" fill="#ef4444" barSize={70} radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
        </Box>
        {renderLegend(barLegendItems)}
      </Card>
    </Stack>
  );
};

export default PrioritySlaCharts;


